import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tooltip } from "@/components/ui/tooltip"
import { Box, HStack, Icon, IconButton, Text, VStack } from "@chakra-ui/react"
import { useState } from "react"
import {
  FaBolt,
  FaClock,
  FaFire,
  FaLayerGroup,
  FaRegQuestionCircle,
} from "react-icons/fa"

export interface StudyHelpButtonProps {
  colorPalette?: string
}

export function StudyHelpButton({ colorPalette }: StudyHelpButtonProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <Tooltip content="How studying works">
        <IconButton
          aria-label="How studying works"
          size="sm"
          variant="ghost"
          colorPalette={colorPalette}
          onClick={() => setIsOpen(true)}
        >
          <Icon as={FaRegQuestionCircle} />
        </IconButton>
      </Tooltip>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>How studying works</DialogTitle>
        </DialogHeader>
        <DialogBody pb={6}>
          <VStack align="stretch" gap={6}>
            {/* Spaced repetition section */}
            <Box>
              <HStack gap={2} mb={3}>
                <Icon as={FaClock} color="teal.500" boxSize={4} />
                <Text fontWeight="bold" fontSize="md">
                  Spaced repetition
                </Text>
              </HStack>
              <VStack align="stretch" gap={2} pl={6}>
                <Text fontSize="sm">
                  After you reveal a card, rate how well you knew it from{" "}
                  <strong>1 (Not at all)</strong> to{" "}
                  <strong>5 (Perfectly)</strong>.
                </Text>
                <Text fontSize="sm">
                  Rate low (1–2) and the card comes back soon — usually the next
                  day.
                </Text>
                <Text fontSize="sm">
                  Rate high (3–5) and it spaces out: first about a day, then ~6
                  days, then longer each time you get it right.
                </Text>
                <Text fontSize="sm">
                  New cards and ones you find hard reappear daily until they
                  stick. That's by design — it's not a reset.
                </Text>
              </VStack>
            </Box>

            {/* Study sessions section */}
            <Box>
              <HStack gap={2} mb={3}>
                <Icon as={FaLayerGroup} color="teal.500" boxSize={4} />
                <Text fontWeight="bold" fontSize="md">
                  Study sessions
                </Text>
              </HStack>
              <VStack align="stretch" gap={2} pl={6}>
                <HStack align="start" gap={2}>
                  <Icon
                    as={FaBolt}
                    color="yellow.500"
                    boxSize={3}
                    mt="3px"
                    flexShrink={0}
                  />
                  <Text fontSize="sm">
                    <strong>Cards per session:</strong> pick 25, 50, or 100 (in
                    Settings or on the Study page) so every session is
                    finishable.
                  </Text>
                </HStack>
                <HStack align="start" gap={2}>
                  <Icon
                    as={FaLayerGroup}
                    color="teal.500"
                    boxSize={3}
                    mt="3px"
                    flexShrink={0}
                  />
                  <Text fontSize="sm">
                    <strong>Study by subject:</strong> open a subject group and
                    tap "Study these" to review just that subject's due cards.
                  </Text>
                </HStack>
                <HStack align="start" gap={2}>
                  <Icon
                    as={FaFire}
                    color="orange.500"
                    boxSize={3}
                    mt="3px"
                    flexShrink={0}
                  />
                  <Text fontSize="sm">
                    <strong>Custom drill:</strong> filter by the ratings you've
                    given (e.g. review only your 4s) to target weak cards — this
                    ignores the daily schedule.
                  </Text>
                </HStack>
                <HStack align="start" gap={2}>
                  <Icon
                    as={FaClock}
                    color="blue.500"
                    boxSize={3}
                    mt="3px"
                    flexShrink={0}
                  />
                  <Text fontSize="sm">
                    <strong>Resume:</strong> if you leave mid-session, you can
                    pick up where you left off on the same device.
                  </Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </DialogBody>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}
